/**
 * Editing a proposed or planned role from the Org Planning Overview.
 *
 * Talks to `proxy-server/routes/internal-playground-roles.js`. The rules — which rows, which date
 * column, the 10% cost rule — live there and in `lib/plannedRoleEdits.js`; this file only carries
 * the answers across, including the two 409s the edit form turns into a question or a refusal.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { RoleCompensation } from '../types/compensation';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type RoleSource = 'proposal' | 'hiring_plan';

/**
 * The editable values. `startDate` is `Planned Start Date` on a proposal and `Adjusted Planned
 * Start Date` on a hiring-plan row — the proxy decides which, so the form never names a column.
 * `proposalRational` exists only on a proposal.
 */
export interface RoleEditValues {
  title: string;
  role: string;
  peopleTeam: string;
  unit: string;
  trackAndLevel: string;
  reasonForHiring: string;
  costCategory: string;
  hiringManagerEmployeeId: string;
  startDate: string;
  proposalRational?: string;
}

export interface EditableRole {
  source: RoleSource;
  id: string;
  status: string;
  values: RoleEditValues;
  /** The committed planned date of a hiring-plan row, shown read-only beside the adjusted one. */
  plannedStartDate: string | null;
  hiringManagerName: string | null;
  backfillLinked: boolean;
  /** How many of the three approvers have recorded a decision (proposals only). */
  decisions: number;
  compensation: RoleCompensation | null;
  /** A hiring-plan row's change awaiting approval. While set, the row takes no other edit. */
  openChangeRequest: { id: string; requestedBy: string; createdAt: string; descriptions: string[] } | null;
}

export interface CostChange {
  before: RoleCompensation | null;
  after: RoleCompensation | null;
}

/** A 400: which fields to mark. */
export class RoleEditValidationError extends Error {
  details: Partial<Record<keyof RoleEditValues, string>>;
  constructor(message: string, details: Partial<Record<keyof RoleEditValues, string>>) {
    super(message);
    this.details = details;
  }
}

/**
 * 409 `needs_approval`: the edit raises the cost by more than 10% (or cannot be costed), so it goes
 * to the CPO, CEO and CFO — and has not been confirmed yet. `kind` says what confirming does: a
 * proposal is saved and its `cleared` decisions reset; a hiring-plan row is NOT changed, the edit
 * becomes a change request.
 */
export class ApprovalRequiredError extends Error {
  kind: 'reapproval' | 'change_request';
  cleared: number;
  reason: 'cost_increase' | 'uncostable';
  cost: CostChange | null;
  constructor(
    kind: 'reapproval' | 'change_request',
    cleared: number,
    reason: 'cost_increase' | 'uncostable',
    cost: CostChange | null
  ) {
    super('This change needs approval.');
    this.kind = kind;
    this.cleared = cleared;
    this.reason = reason;
    this.cost = cost;
  }
}

/** Any other refusal worth showing verbatim — e.g. 409 `change_pending`, 503 bands unavailable. */
export class RoleEditRefusedError extends Error {
  cost: CostChange | null;
  constructor(message: string, cost: CostChange | null = null) {
    super(message);
    this.cost = cost;
  }
}

const roleUrl = (source: RoleSource, id: string) =>
  `${PROXY_URL}/api/internal-playground/roles/${source}/${encodeURIComponent(id)}`;

export async function fetchEditableRole(source: RoleSource, id: string): Promise<{ role: EditableRole }> {
  const res = await authenticatedFetch(roleUrl(source, id), { suppressErrorStatuses: [404, 409] });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Could not load the role (${res.status})`);
  return body;
}

export interface RoleEditResult {
  changed: boolean;
  reapproval: boolean;
  /** A hiring-plan edit over 10% was filed as a change request and the row left untouched. */
  changeRequested: boolean;
  cleared: number;
  notified: boolean;
  notifyReason: string | null;
}

export async function saveRoleEdit(
  source: RoleSource,
  id: string,
  changes: Partial<RoleEditValues>,
  { confirmApproval = false }: { confirmApproval?: boolean } = {}
): Promise<RoleEditResult> {
  const res = await authenticatedFetch(roleUrl(source, id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ changes, confirmApproval }),
    suppressErrorStatuses: [400, 404, 409, 503],
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 400 && body.details) {
    throw new RoleEditValidationError(body.error || 'The edit is invalid.', body.details);
  }
  if (res.status === 409 && body.code === 'needs_approval') {
    throw new ApprovalRequiredError(body.kind, body.cleared ?? 0, body.reason ?? 'cost_increase', body.cost ?? null);
  }
  if (!res.ok) throw new RoleEditRefusedError(body.error || `Could not save the change (${res.status})`, body.cost ?? null);
  return {
    changed: Boolean(body.changed),
    reapproval: Boolean(body.reapproval),
    changeRequested: Boolean(body.changeRequested),
    cleared: body.cleared ?? 0,
    notified: Boolean(body.notified),
    notifyReason: body.notifyReason ?? null,
  };
}
